import {
  AfterViewInit,
  Component,
  OnInit,
  ViewChild,
  ElementRef,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  DestroyRef,
  inject,
  signal,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Sort } from '@angular/material/sort';
import { PageEvent } from '@angular/material/paginator';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { COMMON_I18N } from '@app/i18n/common.i18n';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { BlobServiceClient } from '@azure/storage-blob';
import { NgxImageCompressService } from 'ngx-image-compress';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { DatabaseCacheService, Section, Breadcrumb } from '@app/data/cache/database-cache.service';
import { CreateMediaEncodingService } from '@app/data/media/create-media-encoding.service';
import { CreateMediaService } from '@app/data/media/create-media.service';
import { GetMediaService } from '@app/data/media/get-media.service';
import { DeleteMediaService } from '@app/data/media/delete-media.service';
import { ListMediasService } from '@app/data/media/list-medias.service';
import { ConfirmDialogComponent } from '@app/shared/components/dialogs/confirm-dialog/confirm-dialog.component';
import { SHARED_IMPORTS } from 'src/app/shared/standalone-imports';
import { ImageViewerDialogComponent } from './ui/image-viewer-dialog.component';
import { MediaVideoPlayerComponent } from './ui/media-video-player.component';
import { USER_MEDIA_I18N } from './user-media.i18n';
import { MediaDocument, UploadTarget } from '@app/data/models/media/media.model';
import {
  createMkplayerInstance,
  destroyMkplayerInstance,
  beginSuppressMkplayerErrors,
  endSuppressMkplayerErrors,
  PLAYER_LOAD_TIMEOUT_MS,
  PLAYER_RETRY_INTERVAL_MS,
  PLAYER_RETRY_MAX,
} from './utils/mkplayer.utils';
import { buildMediaThumbnailUrl } from '@app/shared/utils/media-thumbnail.utils';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const log = (..._args: any[]) => { /* noop — debug logging removed */ };

@Component({
  selector: 'app-user-media',
  templateUrl: './user-media.component.html',
  styleUrls: ['./user-media.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [NgxImageCompressService],
  imports: [...SHARED_IMPORTS, MediaVideoPlayerComponent],
})
export class UserMediaComponent implements OnInit, AfterViewInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly snackBar = inject(MatSnackBar);
  private readonly dialog = inject(MatDialog);
  private readonly common = inject(DatabaseCacheService);
  private readonly imageCompress = inject(NgxImageCompressService);
  private readonly cms = inject(CreateMediaService);
  private readonly ces = inject(CreateMediaEncodingService);
  private readonly getMediaService = inject(GetMediaService);
  private readonly deleteMediaService = inject(DeleteMediaService);
  private readonly listMediasService = inject(ListMediasService);
  private readonly paginatorIntl = inject(MatPaginatorIntl);

  protected readonly i18n = USER_MEDIA_I18N;

  @ViewChild('mediaFileInput') mediaFileInput!: ElementRef<HTMLInputElement>;
  @ViewChildren('mkplayerdiv') mkplayerdiv!: QueryList<ElementRef>;

  breadcrumbs: Breadcrumb[] = [];
  uploadProgress = 0;
  message = '';
  method = '';
  selectedFile: File | null = null;
  selectedFileName = '';
  submitted = false;
  dragOver = false;
  uploading = false;
  refreshing = false;
  mediasList: MediaDocument[] = [];
  selectedSection: Section | null = null;

  private recordMeasurerRole = false;
  private recordRefererRole = false;
  private uploadTarget: UploadTarget | null = null;
  private readonly playerInstances = new Map<string, any>();
  /** プレイヤーごとのロードタイムアウト・リトライ管理 */
  private readonly playerTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private readonly playerRetryCounts = new Map<string, number>();
  private intersectionObserver: IntersectionObserver | null = null;
  private encodingPollTimer: ReturnType<typeof setTimeout> | null = null;
  private encodingPollCount = 0;
  private readonly pendingMediaIds = new Set<string>();


  readonly viewMode = signal<'gallery' | 'list'>('gallery');
  readonly displayedColumns = ['createdAt', 'method', 'comment', 'mediaStatus'];

  // Sort & Pagination state（サーバー側 OFFSET/LIMIT ページング）
  // ソートは createdAt のみ。方向のみ asc/desc をサーバーへ送る。
  readonly sortActive = 'createdAt';
  sortDirection: 'asc' | 'desc' = 'desc';
  pageIndex = 0;
  pageSize = 10;
  readonly pageSizeOptions = [5, 10, 25, 100];
  /** 直近のページ取得でフルページ(=pageSize 件)取れたか。次ページ有無の推定に使う */
  hasNextPage = false;
  /** ページ取得中フラグ（多重取得防止） */
  private loadingPage = false;
  /** section 未準備で初回ロードを保留しているか */
  private pendingInitialLoad = false;

  /** 並び順をサーバーへ渡す形に変換 */
  private get order(): 'asc' | 'desc' {
    return this.sortDirection === 'asc' ? 'asc' : 'desc';
  }

  /**
   * Material paginator 用の length。総件数はサーバーから取らないため、
   * 「フルページ取得できたら次がある」前提で擬似的な length を組み立てる。
   *  - 次ページあり: 現ページの末尾 + 1 を返し「次へ」を活性化
   *  - 次ページなし: 現ページまでの実数を返す
   */
  get paginatorLength(): number {
    const base = this.pageIndex * this.pageSize + this.mediasList.length;
    return this.hasNextPage ? base + 1 : base;
  }

  onSortChange(sort: Sort): void {
    // createdAt 以外は無視（テンプレート上も createdAt のみソート可）
    if (sort.active !== this.sortActive) return;
    this.sortDirection = sort.direction === 'asc' ? 'asc' : 'desc';
    this.pageIndex = 0;
    this.loadPage();
  }

  onPageChange(event: PageEvent): void {
    const pageSizeChanged = event.pageSize !== this.pageSize;
    this.pageSize = event.pageSize;
    // ページサイズ変更時は先頭ページへ戻す
    this.pageIndex = pageSizeChanged ? 0 : event.pageIndex;
    this.loadPage();
  }

  refreshMediaList(): void {
    this.refreshing = true;
    this.cdr.markForCheck();
    this.loadPage();
  }

  readonly fileNotSelectedText = USER_MEDIA_I18N.file.notSelected;

  ngOnInit(): void {
    log('ngOnInit');
    this.configurePaginatorLabels();

    // メディアデータは共有キャッシュではなく専用ページング経路で取得するため、
    // parametersUpdated では section 準備（パンくず・初回ロード保留の解除）のみ扱う。
    this.common.parametersUpdated.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.selectedSection = this.common.getSelectedSection();

      if (this.selectedSection != null) {
        this.breadcrumbs = [
          { label: '', icon: 'home', link: ['/home', this.selectedSection.sectionId] },
          { label: this.i18n.breadcrumb.userList, icon: '', link: ['/home', this.selectedSection.sectionId, 'users'] },
          { label: this.i18n.breadcrumb.media },
        ];

        // section 未準備で保留していた初回ロードをここで実行
        if (this.pendingInitialLoad) {
          this.pendingInitialLoad = false;
          this.loadPage();
        }
      }
    });

    // route.params の Subscribe は parametersUpdated の Subscribe よりあとに行うこと
    this.route.params.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      const userId = params['userId'];
      log('route.params changed', { userId });

      this.common.setSelectedUserId(userId);

      const roles: string[] | null = this.common.getSelectedSectionRoles();
      this.determineRoles(roles);

      // ユーザー切替時は先頭ページから取り直す
      this.pageIndex = 0;

      if (this.recordMeasurerRole || this.recordRefererRole) {
        this.loadPage();
      } else {
        log('SKIP loadPage (no role)', { userId });
      }
    });

    // MKPlayer の初期化は QueryList の変更を検知してトリガーする
    this.destroyRef.onDestroy(() => {
      log('DESTROY', { playerInstances: this.playerInstances.size });
      this.stopEncodingPoll();
      this.destroyAllPlayers();
    });
  }

  ngAfterViewInit(): void {
    log('ngAfterViewInit', { mkplayerdivCount: this.mkplayerdiv.length });

    this.setupIntersectionObserver();

    this.mkplayerdiv.changes.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      log('mkplayerdiv.changes', {
        divCount: this.mkplayerdiv.length,
        existingPlayers: this.playerInstances.size,
      });
      this.observePlayerDivs();
    });
    // 初回レンダリング分もカバー
    if (this.mkplayerdiv.length > 0) {
      log('mkplayerdiv initial render', { count: this.mkplayerdiv.length });
      this.observePlayerDivs();
    }
  }

  navigateToDetail(media: MediaDocument): void {
    const section = this.common.getSelectedSection();
    const userId = this.common.getSelectedUserId();
    if (!section || !userId) return;

    const linkedRecordId = media.data.mediaRecordId?.trim();
    if (this.normalizeValue(media.data.method) === 'vs' && linkedRecordId) {
      this.router.navigate(
        ['/home', section.sectionId, 'users', userId, 'records', linkedRecordId],
        { queryParams: { from: 'medias' } },
      );
      return;
    }

    const mediaId = media.id;
    this.router.navigate(['/home', section.sectionId, 'users', userId, 'medias', mediaId]);
  }

  getStatusLabel(status: string): string {
    switch (this.normalizeValue(status)) {
      case 'prepared': return $localize`:@@home.userMedia.status.preparing:準備中`;
      case 'uploaded': return $localize`:@@home.userMedia.status.uploaded:アップロード済`;
      case 'encoding': return $localize`:@@home.userMedia.status.encoding:エンコード中`;
      case 'encoded': return $localize`:@@home.userMedia.status.encoded:完了`;
      case 'failed': return $localize`:@@home.userMedia.status.failed:失敗`;
      case 'error': return $localize`:@@home.userMedia.status.error:エラー`;
      case 'canceled': return $localize`:@@home.userMedia.status.cancelled:キャンセル`;
      case 'deleted': return $localize`:@@home.userMedia.status.deleted:削除されました`;
      default: return status;
    }
  }

  isImageMedia(media: MediaDocument): boolean {
    return this.normalizeValue(media?.data?.mediaType) === 'image';
  }

  isVideoMedia(media: MediaDocument): boolean {
    return this.normalizeValue(media?.data?.mediaType) === 'video';
  }

  isVideoPending(media: MediaDocument): boolean {
    if (!this.isVideoMedia(media)) return false;
    return this.isEncodingLikeStatus(media.data.mediaStatus);
  }

  isImagePending(media: MediaDocument): boolean {
    return this.isImageMedia(media) && this.normalizeValue(media.data.mediaStatus) === 'prepared';
  }

  isVideoEncoded(media: MediaDocument): boolean {
    return this.isVideoMedia(media) && this.normalizeValue(media.data.mediaStatus) === 'encoded';
  }

  isEncodingLikeStatus(status: string): boolean {
    const normalized = this.normalizeValue(status);
    return normalized === 'prepared' || normalized === 'uploaded' || normalized === 'encoding';
  }

  isDoneStatus(status: string): boolean {
    return this.normalizeValue(status) === 'encoded';
  }

  isErrorStatus(status: string): boolean {
    const normalized = this.normalizeValue(status);
    return normalized === 'failed' || normalized === 'error' || normalized === 'canceled' || normalized === 'cancelled';
  }

  isDeletedStatus(status: string): boolean {
    return this.normalizeValue(status) === 'deleted';
  }

  /** エラー/削除済みでプレビューを表示できないメディア（ギャラリーのプレースホルダ用） */
  isUnavailableMedia(media: MediaDocument): boolean {
    const status = media?.data?.mediaStatus;
    return this.isErrorStatus(status) || this.isDeletedStatus(status);
  }

  mediaFileInputClick(): void {
    this.mediaFileInput.nativeElement.click();
    this.uploadProgress = 0;
  }

  onFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;
    this.setSelectedFile(input.files[0]);
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragOver = false;

    const file = event.dataTransfer?.files?.[0];
    if (!file) return;

    if (!this.isAcceptedFileType(file)) {
      this.snackBar.open(this.i18n.file.unsupportedFormat, this.i18n.button.close, { duration: 3000 });
      return;
    }

    this.setSelectedFile(file);
    this.cdr.markForCheck();
  }

  private setSelectedFile(file: File): void {
    this.selectedFile = file;
    this.selectedFileName = file.name;
    this.message = this.message + ' ' + file.name;
  }

  get hasFormInput(): boolean {
    return this.selectedFile !== null || this.method !== '' || this.message.trim() !== '';
  }

  clearForm(): void {
    this.resetUploadForm();
    if (this.mediaFileInput) {
      this.mediaFileInput.nativeElement.value = '';
    }
    this.cdr.markForCheck();
  }

  private isAcceptedFileType(file: File): boolean {
    const accepted = [
      'video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/x-ms-wmv',
      'video/x-flv', 'video/x-matroska', 'image/jpeg', 'image/gif', 'image/png',
    ];
    return accepted.includes(file.type);
  }

  createMediaRecord(): void {
    this.submitted = true;

    if (this.selectedFile === null || this.method === '') {
      this.cdr.markForCheck();
      return;
    }

    const extension = this.selectedFile.name.split('.').pop();
    let mediaType: string | null = null;
    if (this.selectedFile.type.startsWith('image')) mediaType = 'image';
    if (this.selectedFile.type.startsWith('video')) mediaType = 'video';

    if (mediaType == null) {
      this.snackBar.open(this.i18n.file.unsupportedFormat, this.i18n.button.close, { duration: 3000 });
      return;
    }

    const postParams = {
      givenTo: this.common.getSelectedUserId(),
      comment: this.message,
      mediaType,
      extension,
      method: this.method,
      tags: [],
    };

    log('createMediaRecord', { mediaType, extension, method: this.method });

    this.cms.createMediaRecord(postParams).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (res: any) => {
        const mediaRecord = res as MediaDocument;
        log('createMediaRecord response', { id: mediaRecord.id, type: mediaRecord.data.mediaType });

        this.uploadTarget = {
          id: mediaRecord.id,
          serverRootURL: mediaRecord.data.storage.serverRootURL,
          container: mediaRecord.data.storage.container,
          blobName: mediaRecord.data.storage.blobName,
          blobSasToken: mediaRecord.data.storage.blobSasToken,
          thumbnailName: mediaRecord.data.storage.thumbnailName,
          thumbnailSasToken: mediaRecord.data.storage.thumbnailSasToken,
          mediaType: mediaRecord.data.mediaType,
        };

        this.pendingMediaIds.add(mediaRecord.id);
        this.mediasList = [...this.mediasList, mediaRecord];
        this.uploadMediaFile(this.uploadTarget);
      },
    );
  }

  confirmDeleteMedia(mediaId: string): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      disableClose: false,
      data: {
        title: this.i18n.delete.title,
        message: this.i18n.delete.confirmMessage,
      },
    });

    dialogRef.afterClosed().pipe(takeUntilDestroyed(this.destroyRef)).subscribe((result) => {
      if (result) {
        this.deleteMedia(mediaId);
      }
    });
  }

  showOriginalImage(mediaId: string): void {
    this.getMediaService.getMedia(mediaId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (res: any) => {
        const mediaDocument = (res as MediaDocument[])[0];
        const url = `${mediaDocument.data.storage.serverRootURL}/${mediaDocument.data.storage.container}/${mediaDocument.data.storage.blobName}${mediaDocument.data.storage.blobSasToken}`;

        this.dialog.open(ImageViewerDialogComponent, {
          data: { imageUrl: url },
          panelClass: 'image-viewer-panel',
          maxWidth: '95vw',
          maxHeight: '95vh',
        });
      },
    );
  }

  downloadMedia(mediaId: string): void {
    this.getMediaService.getMedia(mediaId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (res: any) => {
        const mediaDocument = (res as MediaDocument[])[0];
        const url = `${mediaDocument.data.storage.serverRootURL}/${mediaDocument.data.storage.container}/${mediaDocument.data.storage.blobName}${mediaDocument.data.storage.blobSasToken}`;
        window.location.href = url;
      },
    );
  }

  getThumbnailUrl(mediaDocument: MediaDocument): string {
    // サムネ未生成（必須欠落 or thumbnailName に動画ファイル名が流用されている）の場合は
    // 空文字を返し、無効 URL への 404 を抑止する。判定は共有ユーティリティに集約。
    return buildMediaThumbnailUrl(mediaDocument.data.storage);
  }

  getOriginalImageUrl(mediaDocument: MediaDocument): string {
    const s = mediaDocument.data.storage;
    return `${s.serverRootURL}/${s.container}/${s.blobName}${s.blobSasToken}`;
  }

  onThumbnailError(event: Event, mediaDocument: MediaDocument): void {
    const image = event.target as HTMLImageElement | null;
    if (!image) return;
    const fallbackUrl = this.getOriginalImageUrl(mediaDocument);
    if (image.src !== fallbackUrl) {
      image.src = fallbackUrl;
    }
  }

  getPlayerInstance(resid: string): any {
    return this.playerInstances.get(resid) ?? null;
  }

  // --- Private methods ---

  private setupIntersectionObserver(): void {
    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          const el = entry.target as HTMLElement;
          const resid = el.getAttribute('resid');
          if (!resid || this.playerInstances.has(resid)) return;

          // ビューポートに入ったら observe を解除して初期化
          this.intersectionObserver?.unobserve(el);
          this.initializePlayer(el, resid);
        });
      },
      { rootMargin: '200px' }, // 200px 手前から初期化開始
    );

    log('IntersectionObserver setup', { rootMargin: '200px' });
  }

  private observePlayerDivs(): void {
    let observed = 0;
    this.mkplayerdiv.forEach((mkRef) => {
      const el = mkRef.nativeElement as HTMLElement;
      const resid = el.getAttribute('resid');
      if (!resid || this.playerInstances.has(resid)) return;

      this.intersectionObserver?.observe(el);
      observed++;
    });
    log('observePlayerDivs', { observed, existingPlayers: this.playerInstances.size });
  }

  private initializePlayer(el: HTMLElement, resid: string): void {
    const media = this.mediasList.find((m) => m.id === resid);
    if (!media?.data?.storage?.streamingURL) {
      return;
    }

    // 既存プレイヤーがあれば先に破棄
    this.destroyPlayer(resid);

    try {
      const instance = createMkplayerInstance(
        el,
        media.data.storage.streamingURL,
        media.data.storage.streamingToken,
        {
          onError: () => this.handlePlayerLoadFailed(el, resid),
          // 「実際に再生可能」になって初めてロード成功とみなす。<video> 挿入だけでは止めない。
          onReady: () => {
            this.clearPlayerTimer(resid);
            this.playerRetryCounts.set(resid, 0);
          },
        },
      );

      this.playerInstances.set(resid, instance);
      this.cdr.markForCheck();

      // ロードタイムアウト: 一定時間内に「再生可能」にならなければリトライ。
      // <video> が挿入されても duration 未確定/readyState 低のまま固まる DRM ケースも拾う。
      this.clearPlayerTimer(resid);
      this.playerTimers.set(resid, setTimeout(() => {
        const dur = instance.getDuration?.() ?? 0;
        const video = el.querySelector('video') as HTMLVideoElement | null;
        const ready = dur > 0 || (video?.readyState ?? 0) >= 2;
        if (!ready) {
          this.handlePlayerLoadFailed(el, resid);
        }
      }, PLAYER_LOAD_TIMEOUT_MS));
    } catch {
      // MKPlayer 初期化自体の例外
    }
  }

  private handlePlayerLoadFailed(el: HTMLElement, resid: string): void {
    this.destroyPlayer(resid);

    const count = (this.playerRetryCounts.get(resid) ?? 0) + 1;
    this.playerRetryCounts.set(resid, count);
    if (count > PLAYER_RETRY_MAX) {
      return;
    }
    // 一定間隔後にリトライ
    this.playerTimers.set(resid, setTimeout(() => {
      // el がまだ DOM 上にあるか確認
      if (!el.isConnected) return;
      this.initializePlayer(el, resid);
    }, PLAYER_RETRY_INTERVAL_MS));
  }

  private destroyPlayer(resid: string): void {
    this.clearPlayerTimer(resid);
    const player = this.playerInstances.get(resid);
    if (player) {
      destroyMkplayerInstance(player);
      this.playerInstances.delete(resid);
    }
  }

  private clearPlayerTimer(resid: string): void {
    const timer = this.playerTimers.get(resid);
    if (timer) {
      clearTimeout(timer);
      this.playerTimers.delete(resid);
    }
  }

  private destroyStalePlayers(newList: MediaDocument[]): void {
    const newIds = new Set(newList.map(m => m.id));
    const staleIds: string[] = [];

    this.playerInstances.forEach((_player, id) => {
      if (!newIds.has(id)) {
        staleIds.push(id);
      }
    });
    staleIds.forEach(id => {
      this.destroyPlayer(id);
      this.playerRetryCounts.delete(id);
    });
  }

  private destroyAllPlayers(): void {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = null;
    }

    beginSuppressMkplayerErrors();
    this.playerInstances.forEach((_player, id) => {
      this.clearPlayerTimer(id);
      try { _player.destroy()?.catch?.(() => {}); } catch { /* noop */ }
    });
    setTimeout(() => endSuppressMkplayerErrors(), 500);
    this.playerInstances.clear();
    this.playerTimers.clear();
    this.playerRetryCounts.clear();
  }

  private deleteMedia(mediaId: string): void {
    log('deleteMedia', { mediaId });
    this.deleteMediaService.deleteMedia(mediaId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      () => {
        this.snackBar.open(this.i18n.delete.success, this.i18n.button.close, { duration: 3000 });
        // 他ビュー（レコード一覧・ダイアログ）の共有キャッシュも整合させる
        this.common.evictUserMediaFromCache(mediaId);
        this.mediasList = this.mediasList.filter((m) => m.id !== mediaId);
        this.pendingMediaIds.delete(mediaId);
        this.destroyPlayer(mediaId);
        this.playerRetryCounts.delete(mediaId);
        this.cdr.markForCheck();
        // ページ件数を正すため現在ページを取り直す
        this.loadPage();
      },
    );
  }

  private async uploadMediaFile(target: UploadTarget): Promise<void> {
    const file = this.selectedFile;
    if (!file) return;
    const mediaType = this.normalizeValue(target.mediaType);

    log('uploadMediaFile start', { id: target.id, type: target.mediaType, fileSize: file.size });

    this.uploading = true;
    this.cdr.markForCheck();

    const uploads: Promise<void>[] = [];

    if (mediaType === 'video') {
      uploads.push(
        this.uploadBlobToAzureStorage(target.serverRootURL, target.container, target.blobName, target.blobSasToken, file, true),
      );
    }

    if (mediaType === 'image') {
      const imageFileWoExif = await this.copyImage(file, 85);
      uploads.push(
        this.uploadBlobToAzureStorage(target.serverRootURL, target.container, target.blobName, target.blobSasToken, imageFileWoExif, true),
      );

      const thumbnail = await this.copyImage(file, 50, 240);
      uploads.push(
        this.uploadBlobToAzureStorage(target.serverRootURL, target.container, target.thumbnailName, target.thumbnailSasToken, thumbnail, false),
      );
    }

    try {
      await Promise.all(uploads);
      log('uploadMediaFile complete', { id: target.id });
      this.uploading = false;
      this.snackBar.open(this.i18n.upload.complete, this.i18n.button.close, { duration: 3000 });
      this.cdr.markForCheck();
      this.resetUploadForm();

      if (mediaType === 'image') {
        this.markMediaStatus(target.id, 'Uploaded');
        this.reloadFirstPage();
        return;
      }

      if (mediaType === 'video') {
        this.ces.createMediaEncording({ mediaId: target.id }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: () => {
            log('encoding started', { id: target.id });
            this.snackBar.open(this.i18n.upload.encodingStarted, this.i18n.button.close, { duration: 3000 });
            this.reloadFirstPage();
          },
          error: () => {
            this.reloadFirstPage();
          },
        });
        return;
      }

      this.reloadFirstPage();
    } catch (err) {
      this.uploading = false;
      this.pendingMediaIds.delete(target.id);
      this.mediasList = this.mediasList.filter((m) => m.id !== target.id);
      log('uploadMediaFile FAILED', { id: target.id, error: err });
      this.snackBar.open(this.i18n.upload.failed, this.i18n.button.close, { duration: 3000 });
      this.cdr.markForCheck();
    }
  }

  private async uploadBlobToAzureStorage(
    serverUri: string, containerName: string, blobName: string, blobSasToken: string,
    file: File, trackProgress: boolean,
  ): Promise<void> {
    const blobServiceClient = new BlobServiceClient(serverUri + blobSasToken);
    const containerClient = blobServiceClient.getContainerClient(containerName);
    const blockBlobClient = containerClient.getBlockBlobClient(blobName);
    const fileSize = file.size;

    await blockBlobClient.uploadBrowserData(file, {
      blobHTTPHeaders: { blobContentType: file.type || 'application/octet-stream' },
      onProgress: (ev) => {
        if (trackProgress) {
          this.uploadProgress = Math.floor((ev.loadedBytes / fileSize) * 100);
          this.cdr.markForCheck();
        }
      },
    });
  }

  private copyImage(file: File, quality: number, maxWidth?: number): Promise<File> {
    return new Promise<File>(async (resolve, reject) => {
      try {
        const size = await this.getImageSize(file);
        const ratio = maxWidth ? (maxWidth * 100) / size.width : 100;

        const reader = new FileReader();
        reader.onload = () => {
          const imageString = reader.result as string;
          this.imageCompress.compressFile(imageString, -1, ratio, quality).then(
            (result) => {
              const [header, payload = ''] = result.split(',', 2);
              const mimeType = /^data:([^;]+);base64$/i.exec(header)?.[1] ?? file.type;
              const binaryString = atob(payload);
              const bytes = new Uint8Array(binaryString.length);
              for (let i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i);
              }
              resolve(new File([bytes], file.name, { type: mimeType || 'image/jpeg' }));
            },
            (error) => reject(error),
          );
        };
        reader.onerror = () => reject(reader.error ?? new Error('Failed to read image file.'));
        reader.readAsDataURL(file);
      } catch (error) {
        reject(error);
      }
    });
  }

  private getImageSize(file: File): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve({ width: img.width, height: img.height });
      img.onerror = () => reject(new Error('Failed to load image.'));
      img.src = URL.createObjectURL(file);
    });
  }

  private markMediaStatus(mediaId: string, status: MediaDocument['data']['mediaStatus']): void {
    this.mediasList = this.mediasList.map((media) => {
      if (media.id !== mediaId) return media;
      return {
        ...media,
        data: {
          ...media.data,
          mediaStatus: status,
        },
      };
    });
    this.cdr.markForCheck();
  }

  private resetUploadForm(): void {
    this.message = '';
    this.selectedFile = null;
    this.selectedFileName = '';
    this.method = '';
    this.submitted = false;
    this.uploadProgress = 0;
  }

  private get hasEncodingItems(): boolean {
    return this.mediasList.some(m =>
      this.isVideoMedia(m) &&
      this.isEncodingLikeStatus(m.data.mediaStatus),
    );
  }

  private updateEncodingPoll(): void {
    if (this.hasEncodingItems && !this.encodingPollTimer) {
      this.encodingPollCount = 0;
      this.scheduleNextEncodingPoll();
    } else if (!this.hasEncodingItems && this.encodingPollTimer) {
      this.stopEncodingPoll();
    }
  }

  private getEncodingPollInterval(): number {
    if (this.encodingPollCount < 5) return 30_000;
    if (this.encodingPollCount < 15) return 60_000;
    return 180_000;
  }

  private scheduleNextEncodingPoll(): void {
    const intervalMs = this.getEncodingPollInterval();
    this.encodingPollTimer = setTimeout(() => {
      this.encodingPollCount++;
      this.loadPage();
      // loadPage 完了時の updateEncodingPoll で、
      // hasEncodingItems が false なら stopEncodingPoll が呼ばれる。
      // まだエンコード中なら次をスケジュール。
      if (this.encodingPollTimer !== null && this.hasEncodingItems) {
        this.scheduleNextEncodingPoll();
      }
    }, intervalMs);
  }

  private stopEncodingPoll(): void {
    if (this.encodingPollTimer) {
      clearTimeout(this.encodingPollTimer);
      this.encodingPollTimer = null;
    }
  }

  /** 先頭ページへ戻して取り直す（アップロード直後など、新着を見せたいとき用） */
  private reloadFirstPage(): void {
    this.pageIndex = 0;
    this.loadPage();
  }

  /**
   * 現在のページ・並び順でサーバーから1ページ分のメディアを取得する。
   * 共有キャッシュ（prepareUserMediasList）は使わず、ギャラリー専用に直接取得する。
   */
  private loadPage(): void {
    const sectionId = this.common.getSelectedSection()?.sectionId;
    const userId = this.common.getSelectedUserId();

    // section 未準備なら初回ロードを保留（parametersUpdated で再開）
    if (!sectionId) {
      this.pendingInitialLoad = true;
      return;
    }
    if (!userId) {
      this.refreshing = false;
      this.cdr.markForCheck();
      return;
    }
    if (this.loadingPage) {
      return;
    }

    this.loadingPage = true;
    this.listMediasService
      .listMedias(sectionId, userId, { count: this.pageSize, skip: this.pageIndex * this.pageSize, order: this.order })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          const page = (res as MediaDocument[]) ?? [];
          this.loadingPage = false;

          // 先頭以外のページが空（削除や stale な深いページ）なら1ページ戻して取り直す
          if (page.length === 0 && this.pageIndex > 0) {
            this.pageIndex -= 1;
            this.loadPage();
            return;
          }

          // フルページ取得 = まだ次がある可能性
          this.hasNextPage = page.length === this.pageSize;

          // 先頭ページかつ新しい順のときだけ、アップロード直後の楽観表示をマージする
          const showPending = this.pageIndex === 0 && this.order === 'desc';
          const newList = showPending ? this.mergePendingMedias(page) : page;

          // 新リストに無い mediaId のプレイヤーを破棄
          this.destroyStalePlayers(newList);
          this.mediasList = newList;

          this.refreshing = false;
          this.cdr.markForCheck();

          // エンコーディング中アイテムの有無でポーリングを制御
          this.updateEncodingPoll();

          // 既存 DOM に対して再観測（未初期化プレイヤーを拾う）
          if (this.intersectionObserver && this.mkplayerdiv?.length > 0) {
            this.observePlayerDivs();
          }
        },
        error: () => {
          this.hasNextPage = false;
          this.refreshing = false;
          this.loadingPage = false;
          this.cdr.markForCheck();
        },
      });
  }

  private determineRoles(roles: string[] | null): void {
    this.recordMeasurerRole = roles?.includes('MEASUREMENT') ?? false;
    this.recordRefererRole = roles?.includes('REFER') ?? false;
  }

  private configurePaginatorLabels(): void {
    this.paginatorIntl.itemsPerPageLabel = COMMON_I18N.paginator.itemsPerPage;
    this.paginatorIntl.nextPageLabel = COMMON_I18N.paginator.nextPage;
    this.paginatorIntl.previousPageLabel = COMMON_I18N.paginator.previousPage;
    this.paginatorIntl.firstPageLabel = COMMON_I18N.paginator.firstPage;
    this.paginatorIntl.lastPageLabel = COMMON_I18N.paginator.lastPage;
    this.paginatorIntl.changes.next();
  }

  private mergePendingMedias(serverMedias: MediaDocument[]): MediaDocument[] {
    if (this.pendingMediaIds.size === 0) {
      return serverMedias;
    }

    const serverIds = new Set(serverMedias.map((m) => m.id));
    for (const id of [...this.pendingMediaIds]) {
      if (serverIds.has(id)) {
        this.pendingMediaIds.delete(id);
      }
    }

    if (this.pendingMediaIds.size === 0) {
      return serverMedias;
    }

    const optimisticOnly = this.mediasList.filter((m) => this.pendingMediaIds.has(m.id) && !serverIds.has(m.id));
    if (!optimisticOnly.length) {
      return serverMedias;
    }

    return [...serverMedias, ...optimisticOnly];
  }

  private normalizeValue(value: string | null | undefined): string {
    return (value ?? '').trim().toLowerCase();
  }
}
